import React from 'react';
import classNames from 'classnames';

import { propTypes } from '../../../util/types';
import { ListingCard, PaginationLinks } from '../../../components';

import ISOResultCard from './ISOResultCard';
import MarketplaceListingCard from './MarketplaceListingCard';
import css from './SearchResultsPanel.module.css';

const ISO_LISTING_TYPE = 'iso-book';

/**
 * SearchResultsPanel component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.listing>} props.listings - The listings
 * @param {propTypes.pagination} props.pagination - The pagination
 * @param {Object} props.search - The search
 * @param {Function} props.setActiveListing - The function to handle the active listing
 * @param {boolean} [props.isMapVariant] - Whether the map variant is enabled
 * @returns {JSX.Element}
 */
const SearchResultsPanel = props => {
  const {
    className,
    rootClassName,
    listings = [],
    pagination,
    search,
    setActiveListing,
    isMapVariant = true,
    listingTypeParam,
    paginationPageName,
    intl,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const pageName = paginationPageName || (listingTypeParam ? 'SearchPageWithListingType' : 'SearchPage');
  const isISOResults = listingTypeParam === ISO_LISTING_TYPE;
  const isMarketplaceResults = !listingTypeParam;
  const pagePathParams = listingTypeParam ? { listingType: listingTypeParam } : undefined;

  const paginationLinks =
    pagination && pagination.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={pageName}
        pagePathParams={pagePathParams}
        pageSearchParams={search}
        pagination={pagination}
        aria-label={intl.formatMessage({ id: 'SearchResultsPanel.screenreader.pagination' })}
      />
    ) : null;

  const cardRenderSizes = isMapVariant => {
    if (isMapVariant) {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 767px) 100vw',
        `(max-width: 1023px) ${panelMediumWidth}vw`,
        `(max-width: 1920px) ${panelLargeWidth / 2}vw`,
        `${panelLargeWidth / 3}vw`,
      ].join(', ');
    } else {
      // Panel width relative to the viewport
      const panelMediumWidth = 50;
      const panelLargeWidth = 62.5;
      return [
        '(max-width: 549px) 100vw',
        '(max-width: 767px) 50vw',
        `(max-width: 1439px) 26vw`,
        `(max-width: 1920px) 18vw`,
        `14vw`,
      ].join(', ');
    }
  };

  return (
    <div className={classes}>
      <ul
        className={classNames({
          [css.listingCardsMapVariant]: isMapVariant && !isISOResults,
          [css.listingCards]: !isMapVariant && !isISOResults,
          [css.isoListingCards]: isISOResults,
        })}
      >
        {listings.map(l => (
          <li key={l.id.uuid} className={css.resultItem}>
            {isISOResults ? (
              <ISOResultCard listing={l} />
            ) : isMarketplaceResults ? (
              <MarketplaceListingCard listing={l} renderSizes={cardRenderSizes(isMapVariant)} />
            ) : (
              <ListingCard
                className={css.listingCard}
                listing={l}
                renderSizes={cardRenderSizes(isMapVariant)}
                setActiveListing={setActiveListing}
              />
            )}
          </li>
        ))}
        {props.children}
      </ul>
      {paginationLinks}
    </div>
  );
};

export default SearchResultsPanel;
